/**
 * CaptchaService
 * 
 * Provides cryptographic challenge generation, validation, anti-replay tokens,
 * canvas rendering, and bot protection utilities for user registration.
 */

export interface CaptchaChallenge {
  id: string;
  timestamp: number;
  signature: string;
}

export interface InternalChallenge extends CaptchaChallenge {
  text: string;
}

export interface VerifyChallengeResult {
  success: boolean;
  token?: string;
  error?: string;
}

// Unambiguous characters: avoids 0, O, 1, I, l which confuse users
const CAPTCHA_CHARS = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
const CHALLENGE_LENGTH = 6;
const CHALLENGE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MIN_SOLVE_DURATION_MS = 500; // Fast submissions are automated bots
const CAPTCHA_SALT = process.env.REACT_APP_CAPTCHA_SECRET || 'tp_sec_salt_v1_9a8b7c';

// Store of consumed tokens with consumption timestamp to prevent replay attacks
const consumedTokens = new Map<string, number>();

/**
 * Clean up expired consumed tokens to prevent memory leaks while keeping active ones
 */
function pruneConsumedTokens(): void {
  const now = Date.now();
  for (const [token, timestamp] of consumedTokens.entries()) {
    if (now - timestamp > CHALLENGE_TTL_MS) {
      consumedTokens.delete(token);
    }
  }
  // Hard cap to prevent memory bloat under denial-of-service attempts
  if (consumedTokens.size > 2000) {
    const keys = Array.from(consumedTokens.keys()).slice(0, 1000);
    for (const k of keys) {
      consumedTokens.delete(k);
    }
  }
}

/**
 * Access Web Crypto API safely across browser and Node environments
 */
function getCrypto(): Crypto {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    return window.crypto;
  }
  if (typeof globalThis !== 'undefined' && (globalThis as unknown as { crypto?: Crypto }).crypto?.subtle) {
    return (globalThis as unknown as { crypto: Crypto }).crypto;
  }
  throw new Error('Web Crypto API is not available in this environment');
}

/**
 * Computes SHA-256 hex string using Web Crypto API
 */
export async function sha256Hex(plainText: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plainText);
  const cryptoObj = getCrypto();
  const hashBuffer = await cryptoObj.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export class CaptchaService {
  /**
   * Generates a new random challenge string
   */
  private static generateRandomText(length: number = CHALLENGE_LENGTH): string {
    const cryptoObj = getCrypto();
    const randomBytes = new Uint8Array(length);
    cryptoObj.getRandomValues(randomBytes);
    let result = '';
    for (let i = 0; i < length; i++) {
      result += CAPTCHA_CHARS[randomBytes[i] % CAPTCHA_CHARS.length];
    }
    return result;
  }

  /**
   * Generates a new CAPTCHA challenge with cryptographic signature
   */
  static async generateChallenge(): Promise<InternalChallenge> {
    const id = `cap_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const text = this.generateRandomText(CHALLENGE_LENGTH);
    const timestamp = Date.now();
    const signature = await sha256Hex(`${id}:${timestamp}:${text}:${CAPTCHA_SALT}`);

    return {
      id,
      text,
      timestamp,
      signature,
    };
  }

  /**
   * Verifies user response against a challenge and issues a single-use token
   */
  static async verifyChallenge(
    userInput: string,
    challenge: InternalChallenge,
    clientStartedAt?: number
  ): Promise<VerifyChallengeResult> {
    const now = Date.now();

    // Check expiration
    if (now - challenge.timestamp > CHALLENGE_TTL_MS) {
      return {
        success: false,
        error: 'Verification code has expired. Please try with a new code.',
      };
    }

    // Check minimum human response duration if clientStartedAt is provided
    if (clientStartedAt) {
      const elapsed = now - clientStartedAt;
      if (elapsed >= 0 && elapsed < MIN_SOLVE_DURATION_MS) {
        return {
          success: false,
          error: 'Verification completed too quickly. Please try again.',
        };
      }
    }

    // Verify challenge integrity signature
    const expectedChallengeSig = await sha256Hex(
      `${challenge.id}:${challenge.timestamp}:${challenge.text}:${CAPTCHA_SALT}`
    );
    if (challenge.signature !== expectedChallengeSig) {
      return {
        success: false,
        error: 'Invalid challenge signature.',
      };
    }

    // Normalize and compare
    const cleanInput = (userInput || '').replace(/[\s-]+/g, '').toUpperCase();
    const cleanTarget = challenge.text.replace(/[\s-]+/g, '').toUpperCase();

    if (cleanInput !== cleanTarget) {
      return {
        success: false,
        error: 'Incorrect verification code. Please try again.',
      };
    }

    // Generate signed single-use verification token
    const tokenTimestamp = Date.now();
    const tokenSignature = await sha256Hex(
      `${challenge.id}:${tokenTimestamp}:${CAPTCHA_SALT}`
    );
    const token = `tp_cap_${challenge.id}.${tokenTimestamp}.${tokenSignature}`;

    return {
      success: true,
      token,
    };
  }

  /**
   * Validates a verification token (used before creating an account)
   */
  static async verifyToken(token?: string): Promise<boolean> {
    // In test environment, allow pre-seeded test tokens
    if (process.env.NODE_ENV === 'test' && token === 'test-captcha-token') {
      return true;
    }

    if (!token || typeof token !== 'string') {
      return false;
    }

    // Check third-party tokens (reCAPTCHA / Turnstile) only if the service is configured
    if (token.startsWith('recaptcha_')) {
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (!siteKey) return false;
      const rawToken = token.substring('recaptcha_'.length);
      return rawToken.length >= 50;
    }

    if (token.startsWith('turnstile_')) {
      const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
      if (!siteKey) return false;
      const rawToken = token.substring('turnstile_'.length);
      return rawToken.length >= 50;
    }

    // Check internal token format
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    const [prefixAndId, timestampStr, signature] = parts;
    if (!prefixAndId.startsWith('tp_cap_')) {
      return false;
    }

    const timestamp = parseInt(timestampStr, 10);
    if (isNaN(timestamp)) {
      return false;
    }

    const now = Date.now();
    // Must not be older than TTL, and not from the future
    if (now - timestamp > CHALLENGE_TTL_MS || timestamp > now + 60000) {
      return false;
    }

    // Prevent token replay
    if (consumedTokens.has(token)) {
      return false;
    }

    const challengeId = prefixAndId.substring('tp_cap_'.length);
    const expectedSignature = await sha256Hex(
      `${challengeId}:${timestamp}:${CAPTCHA_SALT}`
    );

    if (signature !== expectedSignature) {
      return false;
    }

    // Mark token as consumed with current timestamp
    consumedTokens.set(token, now);
    pruneConsumedTokens();

    return true;
  }

  /**
   * Checks if an email uses a reserved RFC 2606 example domain or subdomain (commonly used by bots)
   */
  static isReservedExampleEmail(email?: string): boolean {
    if (!email || typeof email !== 'string') return false;
    const normalized = email.toLowerCase().trim().replace(/\.$/, '');
    const atIndex = normalized.lastIndexOf('@');
    if (atIndex === -1) return false;

    const domain = normalized.substring(atIndex + 1);
    if (!domain) return false;

    const reservedBases = [
      'example.com',
      'example.org',
      'example.net',
      'example.edu',
    ];

    if (domain === 'example' || domain.endsWith('.example')) {
      return true;
    }

    for (const base of reservedBases) {
      if (domain === base || domain.endsWith(`.${base}`)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Renders the distorted CAPTCHA characters to an HTML canvas
   */
  static renderCaptchaCanvas(canvas: HTMLCanvasElement, text: string): void {
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext('2d');
    } catch (_e) {
      return;
    }
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // 1. Background gradient
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#f8fafc');
    gradient.addColorStop(0.5, '#f1f5f9');
    gradient.addColorStop(1, '#e2e8f0');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // 2. Background curved interference lines
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = `rgba(${50 + Math.floor(Math.random() * 100)}, ${50 + Math.floor(Math.random() * 100)}, ${50 + Math.floor(Math.random() * 100)}, 0.25)`;
      ctx.lineWidth = 1 + Math.random() * 1.5;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.bezierCurveTo(
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height,
        Math.random() * width, Math.random() * height
      );
      ctx.stroke();
    }

    // 3. Background noise dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(${Math.floor(Math.random() * 150)}, ${Math.floor(Math.random() * 150)}, ${Math.floor(Math.random() * 150)}, 0.3)`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1 + Math.random(), 0, Math.PI * 2);
      ctx.fill();
    }

    // 4. Distorted characters
    const charSpacing = (width - 32) / text.length;
    const fonts = ['sans-serif', 'serif', 'monospace', 'Arial', 'Georgia', 'Courier New'];
    const colors = ['#0f172a', '#1e293b', '#334155', '#1e3a8a', '#065f46', '#7c2d12', '#4c1d95', '#0369a1'];

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const x = 20 + i * charSpacing + (Math.random() * 4 - 2);
      const y = height / 2 + (Math.random() * 6 - 3);
      const angle = (Math.random() * 32 - 16) * (Math.PI / 180);
      const fontSize = 22 + Math.floor(Math.random() * 6);
      const font = fonts[Math.floor(Math.random() * fonts.length)];

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.font = `bold ${fontSize}px ${font}`;
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
      ctx.textBaseline = 'middle';
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }

    // 5. Foreground interference line crossing characters
    for (let i = 0; i < 2; i++) {
      ctx.strokeStyle = `rgba(${Math.floor(Math.random() * 80)}, ${Math.floor(Math.random() * 80)}, ${Math.floor(Math.random() * 80)}, 0.35)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * 15, Math.random() * height);
      ctx.lineTo(width - Math.random() * 15, Math.random() * height);
      ctx.stroke();
    }
  }

  /**
   * Accessible speech synthesis for visually impaired users
   */
  static speakCaptcha(text?: string): void {
    if (!text || typeof text !== 'string') {
      return;
    }
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return;
    }
    try {
      if (typeof SpeechSynthesisUtterance === 'undefined') {
        return;
      }
      window.speechSynthesis.cancel();
      const spelledOut = text.split('').join(' . ');
      const utterance = new SpeechSynthesisUtterance(`Verification code: ${spelledOut}`);
      utterance.rate = 0.8;
      window.speechSynthesis.speak(utterance);
    } catch (_e) {
      // Speech synthesis fallback fails silently
    }
  }
}
